// src/pages/pedidos/WhatsappWeb.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaLink, FaMicrophone, FaPaperPlane, FaPlus, FaRegSmile } from 'react-icons/fa';
import { FiArrowLeft, FiDownload, FiImage, FiPaperclip, FiSearch } from 'react-icons/fi';
import { fetchBlobData, fetchData } from '../../services/Api';
import { useMessage } from '../../contexts/geral/MessageContext';
import { usePedido } from '../../contexts/pedidos/PedidoContext';
import { Pedido } from '../../models/pedidos/Pedido';
import CadPedido from './CadPedido';
import { useWhatsAppWeb } from '../../contexts/pedidos/WhatsappWebContext';
import { useChatContato } from '../../contexts/pedidos/ChatContatoContext';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import InputCheck from '../../components/input/InputCheck';
import { readAvatarFromStorage, saveAvatarToStorage } from '../../utils/whatsappAvatarStorage';
import './WhatsappWeb.css';
import { useAlert } from '../../contexts/geral/AlertContext';

const TIPO_MENSAGEM = {
  TEXTO: 1,
  REACAO: 2,
  AUDIO: 3,
  IMAGEM: 4,
  STICKER: 5,
  VIDEO: 6,
  DOCUMENTO: 7,
  MENCAO: 8,
  ATUALIZADO: 9,
  DELETADO: 10,
};

const formatHorario = (dataHora) => {
  if (!dataHora) return '';
  const date = new Date(dataHora);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const formatHorarioContato = (dataHora) => {
  if (!dataHora) return '';
  const date = new Date(dataHora);
  if (Number.isNaN(date.getTime())) return '';

  const agora = new Date();
  const ehHoje =
    date.getDate() === agora.getDate() &&
    date.getMonth() === agora.getMonth() &&
    date.getFullYear() === agora.getFullYear();

  if (ehHoje) {
    return formatHorario(dataHora);
  }

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDataHoraContatoCompleta = (dataHora) => {
  if (!dataHora) return '';
  const date = new Date(dataHora);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDataHoraMensagem = (dataHora) => {
  if (!dataHora) return '';
  const date = new Date(dataHora);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizarBase64 = (valor) => {
  if (!valor || typeof valor !== 'string') return '';
  return valor
    .replace(/^data:.*;base64,/, '')
    .replace(/\s/g, '');
};

const getMimeType = (tipoMensagem) => {
  switch (Number(tipoMensagem)) {
    case TIPO_MENSAGEM.IMAGEM:
      return 'image/jpeg';
    case TIPO_MENSAGEM.STICKER:
      return 'image/webp';
    case TIPO_MENSAGEM.AUDIO:
      return 'audio/ogg';
    case TIPO_MENSAGEM.VIDEO:
      return 'video/mp4';
    default:
      return 'application/octet-stream';
  }
};

const limparTextoMidia = (texto) => {
  const valor = (texto || '').trim();
  if (!valor) return '';
  const partes = valor.split('|');
  return (partes[0] || '').trim();
};

const getTipoMensagemLabel = (tipoMensagem) => {
  switch (Number(tipoMensagem)) {
    case TIPO_MENSAGEM.IMAGEM:
      return 'Imagem';
    case TIPO_MENSAGEM.STICKER:
      return 'Sticker';
    case TIPO_MENSAGEM.AUDIO:
      return 'Audio';
    case TIPO_MENSAGEM.VIDEO:
      return 'Video';
    case TIPO_MENSAGEM.DOCUMENTO:
      return 'Documento';
    case TIPO_MENSAGEM.MENCAO:
      return 'Mencao';
    case TIPO_MENSAGEM.DELETADO:
      return 'Apagada';
    default:
      return 'Mensagem';
  }
};

const getContatoIdValue = (contato) => (
  contato?.contatoIdApi ??
  contato?.contatoId ??
  contato?.chatBotContatoId ??
  contato?.whatsAppContatoId ??
  contato?.whatsappContatoId ??
  contato?.id ??
  null
);

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
  reader.onerror = () => reject(new Error('Nao foi possivel converter a imagem do contato.'));
  reader.readAsDataURL(blob);
});

function ContactAvatar({ contato, avatarUrl, large = false }) {
  const inicial = (contato?.nome || contato?.telefone || '?').charAt(0);

  if (avatarUrl) {
    return (
      <img
        className={`whatsapp-avatar ${large ? 'large' : ''} has-image`}
        src={avatarUrl}
        alt={contato?.nome || contato?.telefone || 'Contato'}
      />
    );
  }

  return (
    <div className={`whatsapp-avatar ${large ? 'large' : ''}`}>
      {inicial}
    </div>
  );
}

function LazyMediaMessage({ msg, textoMidia, mediaDataUrl, mediaLoading, mediaError, onVisible }) {
  const itemRef = useRef(null);
  const isImagem = Number(msg?.tipoMensagem) === TIPO_MENSAGEM.IMAGEM;
  const isSticker = Number(msg?.tipoMensagem) === TIPO_MENSAGEM.STICKER;
  const isAudio = Number(msg?.tipoMensagem) === TIPO_MENSAGEM.AUDIO;
  const isVideo = Number(msg?.tipoMensagem) === TIPO_MENSAGEM.VIDEO;

  useEffect(() => {
    if (!itemRef.current || mediaDataUrl || mediaError || !msg?.idMessage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible(msg.idMessage, msg.tipoMensagem);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '120px 0px', threshold: 0.2 }
    );

    observer.observe(itemRef.current);

    return () => observer.disconnect();
  }, [mediaDataUrl, mediaError, msg?.idMessage, msg?.tipoMensagem, onVisible]);

  return (
    <div className="whatsapp-media-content" ref={itemRef}>
      {mediaDataUrl ? (
        <>
          {isImagem ? (
            <>
              <img className="whatsapp-media-image" src={mediaDataUrl} alt="Imagem do WhatsApp" />
              <a
                className="whatsapp-media-download"
                href={mediaDataUrl}
                download={`imagem-whatsapp-${msg.id}.jpg`}
              >
                <FiDownload />
                Baixar imagem
              </a>
            </>
          ) : null}
          {isSticker ? (
            <>
              <img className="whatsapp-media-sticker" src={mediaDataUrl} alt="Sticker do WhatsApp" />
              <a
                className="whatsapp-media-download"
                href={mediaDataUrl}
                download={`sticker-whatsapp-${msg.id}.webp`}
              >
                <FiDownload />
                Baixar sticker
              </a>
            </>
          ) : null}
          {isAudio ? (
            <audio controls preload="none" className="whatsapp-media-audio" src={mediaDataUrl}>
              Seu navegador nao suporta reproducao de audio.
            </audio>
          ) : null}
          {isVideo ? (
            <>
              <video controls preload="metadata" className="whatsapp-media-video" src={mediaDataUrl}>
                Seu navegador nao suporta reproducao de video.
              </video>
              <a
                className="whatsapp-media-download"
                href={mediaDataUrl}
                download={`video-whatsapp-${msg.id}.mp4`}
              >
                <FiDownload />
                Baixar video
              </a>
            </>
          ) : null}
        </>
      ) : (
        <div className="whatsapp-media-placeholder">
          {mediaError
            ? `Nao foi possivel carregar ${isAudio ? 'o audio' : (isVideo ? 'o video' : (isSticker ? 'o sticker' : 'a imagem'))}.`
            : (mediaLoading
              ? `Carregando ${isAudio ? 'audio' : (isVideo ? 'video' : (isSticker ? 'sticker' : 'imagem'))}...`
              : `${isAudio ? 'Audio' : (isVideo ? 'Video' : (isSticker ? 'Sticker' : 'Imagem'))} aguardando visibilidade...`)}
        </div>
      )}
      {textoMidia && <p>{textoMidia}</p>}
    </div>
  );
}

function WhatsAppWeb() {
  const { showMessage } = useMessage();
  const { showAlert } =useAlert();
  const { setPedidoSel } = usePedido();
  const {
    contatos,
    contatoSelecionado,
    mensagensContato,
    loadingContatos,
    loadingMensagens,
    enviarMensagemTexto,
    enviarUrlApp,
    selecionarContato
  } = useWhatsAppWeb();
  const { savingContato, updateChatContato } = useChatContato();

  const [mensagem, setMensagem] = useState('');
  const [filtroConversas, setFiltroConversas] = useState('');
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);
  const [enviandoUrlApp, setEnviandoUrlApp] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isModalCadPedidoOpen, setIsModalCadPedidoOpen] = useState(false);
  const [showModalEditarContato, setShowModalEditarContato] = useState(false);
  const [nomeContatoEdicao, setNomeContatoEdicao] = useState('');
  const [desativarIAContatoEdicao, setDesativarIAContatoEdicao] = useState(false);
  const [showSidebarMobile, setShowSidebarMobile] = useState(!contatoSelecionado);
  const [mediaByMessageId, setMediaByMessageId] = useState({});
  const [loadingMediaByMessageId, setLoadingMediaByMessageId] = useState({});
  const [errorMediaByMessageId, setErrorMediaByMessageId] = useState({});
  const [avatarByContactId, setAvatarByContactId] = useState({});
  const [, setLoadingAvatarByContactId] = useState({});
  const loadingAvatarByContactIdRef = useRef({});
  const errorAvatarByContactIdRef = useRef({});
  const mensagensRef = useRef(null);
  const emojisDisponiveis = ['😀', '😁', '😂', '🙂', '😉', '😍', '😘', '🙏', '👏', '👍', '🎉', '❤️'];

  useEffect(() => {
    setShowEmojiPicker(false);
    setShowModalEditarContato(false);
    setNomeContatoEdicao('');
    setDesativarIAContatoEdicao(false);
    setMediaByMessageId({});
    setLoadingMediaByMessageId({});
    setErrorMediaByMessageId({});
  }, [contatoSelecionado?.id]);

  useEffect(() => {
    if (!contatoSelecionado) {
      setShowSidebarMobile(true);
    }
  }, [contatoSelecionado]);

  useEffect(() => {
    if (!Array.isArray(contatos) || !contatos.length) return;

    const avatarsFromStorage = {};
    contatos.forEach((contato) => {
      const contatoId = getContatoIdValue(contato);
      const avatarSalvo = readAvatarFromStorage(contatoId);
      if (avatarSalvo) {
        avatarsFromStorage[contatoId] = avatarSalvo;
      }
    });

    if (!Object.keys(avatarsFromStorage).length) return;

    setAvatarByContactId((prev) => ({ ...avatarsFromStorage, ...prev }));
  }, [contatos]);

  const conversasFiltradas = useMemo(() => {
    const termo = filtroConversas.trim().toLowerCase();
    if (!termo) return contatos;

    return contatos.filter((contato) => {
      const nomeContato = (contato.nome || '').toLowerCase();
      const telefone = (contato.telefone || '').toLowerCase();
      const ultimaMensagem = (contato.ultimaMensagem || '').toLowerCase();

      return (
        nomeContato.includes(termo) ||
        telefone.includes(termo) ||
        ultimaMensagem.includes(termo)
      );
    });
  }, [contatos, filtroConversas]);

  const mensagensExibidas = useMemo(() => {
    return (mensagensContato || []).map((msg) => ({
      id: msg.id ?? `${msg.idMessage ?? 'msg'}-${msg.dataHora ?? ''}`,
      texto: msg.messageText || '',
      tipo: msg.fromMe ? 'recebida' : 'enviada',
      horario: formatHorario(msg.dataHora),
      dataHoraLabel: formatDataHoraMensagem(msg.dataHora),
      tipoMensagem: msg.tipo ?? 1,
      idMessage: msg.idMessage ?? null,
      idReact: msg.idReact ?? null,
      fromMe: !!msg.fromMe,
    }));
  }, [mensagensContato]);

  const mensagensRenderizadas = useMemo(() => {
    const mensagensNormais = [];
    const reacoesPorMensagem = new Map();
    const atualizacoesPorMensagem = new Map();
    const exclusoesPorMensagem = new Map();
    const mensagensPorId = new Map();

    (mensagensExibidas || []).forEach((msg) => {
      if (msg.idMessage) {
        mensagensPorId.set(msg.idMessage, msg);
      }
      if (msg.tipoMensagem === TIPO_MENSAGEM.REACAO && msg.idReact) {
        const lista = reacoesPorMensagem.get(msg.idReact) || [];
        lista.push(msg);
        reacoesPorMensagem.set(msg.idReact, lista);
        return;
      }
      if (msg.tipoMensagem === TIPO_MENSAGEM.ATUALIZADO) {
        const alvoId = msg.idReact || msg.idMessage;
        if (alvoId) {
          atualizacoesPorMensagem.set(alvoId, msg);
        }
        return;
      }
      if (msg.tipoMensagem === TIPO_MENSAGEM.DELETADO) {
        const alvoId = msg.idReact || msg.idMessage;
        if (alvoId) {
          exclusoesPorMensagem.set(alvoId, msg);
        }
        return;
      }

      mensagensNormais.push(msg);
    });

    return mensagensNormais.map((msg) => {
      const eventoAtualizacao = msg.idMessage ? atualizacoesPorMensagem.get(msg.idMessage) : null;
      const eventoExclusao = msg.idMessage ? exclusoesPorMensagem.get(msg.idMessage) : null;
      const isDeleted = !!eventoExclusao;
      const isEdited = !!eventoAtualizacao && !isDeleted;
      const textoExibicao = isDeleted
        ? (eventoExclusao?.texto || 'Mensagem deletada')
        : (isEdited ? (eventoAtualizacao?.texto || msg.texto) : msg.texto);

      const reacoes = msg.idMessage ? (reacoesPorMensagem.get(msg.idMessage) || []) : [];
      const contador = {};

      reacoes.forEach((r) => {
        const emoji = (r.texto || '').trim();
        if (!emoji) return;
        contador[emoji] = (contador[emoji] || 0) + 1;
      });

      const reacoesLabel = Object.entries(contador)
        .map(([emoji, qtd]) => (qtd > 1 ? `${emoji} ${qtd}` : emoji))
        .join(' ');

      let mencaoRef = null;
      if (Number(msg.tipoMensagem) === TIPO_MENSAGEM.MENCAO && msg.idReact) {
        const mensagemReferenciada = mensagensPorId.get(msg.idReact);
        if (mensagemReferenciada) {
          const atualizacaoRef = atualizacoesPorMensagem.get(msg.idReact);
          const exclusaoRef = exclusoesPorMensagem.get(msg.idReact);
          const refDeletada = !!exclusaoRef;
          const textoRefBruto = refDeletada
            ? (exclusaoRef?.texto || 'Mensagem deletada')
            : (atualizacaoRef?.texto || mensagemReferenciada.texto);
          const textoRef = limparTextoMidia(textoRefBruto) || '(sem texto)';
          mencaoRef = {
            texto: textoRef,
            tipoLabel: getTipoMensagemLabel(refDeletada ? TIPO_MENSAGEM.DELETADO : mensagemReferenciada.tipoMensagem),
            isDeleted: refDeletada,
          };
        }
      }

      return {
        ...msg,
        texto: textoExibicao,
        isEdited,
        isDeleted,
        reacoesLabel,
        mencaoRef,
      };
    });
  }, [mensagensExibidas]);

  useEffect(() => {
    const el = mensagensRef.current;
    if (!el || loadingMensagens) return;
    el.scrollTop = el.scrollHeight;
  }, [contatoSelecionado?.id, mensagensExibidas.length, loadingMensagens]);

  const getContatoIdSelecionado = () => getContatoIdValue(contatoSelecionado);

  const carregarImagemContato = useCallback(async (contato) => {
    const contatoId = getContatoIdValue(contato);
    if (!contatoId) return '';

    if (avatarByContactId[contatoId]) {
      return avatarByContactId[contatoId];
    }

    if (errorAvatarByContactIdRef.current[contatoId]) {
      return '';
    }

    const avatarSalvo = readAvatarFromStorage(contatoId);
    if (avatarSalvo) {
      setAvatarByContactId((prev) => (
        prev[contatoId] ? prev : { ...prev, [contatoId]: avatarSalvo }
      ));
      return avatarSalvo;
    }

    if (loadingAvatarByContactIdRef.current[contatoId]) {
      return '';
    }

    loadingAvatarByContactIdRef.current[contatoId] = true;
    setLoadingAvatarByContactId((prev) => ({ ...prev, [contatoId]: true }));
    try {
      const blob = await fetchBlobData('/WhatsAppWeb/GetImagemContato', { contatoId });
      if (!blob || !blob.size) {
        errorAvatarByContactIdRef.current[contatoId] = true;
        return '';
      }

      const dataUrl = await blobToDataUrl(blob);
      if (!dataUrl) {
        errorAvatarByContactIdRef.current[contatoId] = true;
        return '';
      }

      saveAvatarToStorage(contatoId, dataUrl);
      setAvatarByContactId((prev) => ({ ...prev, [contatoId]: dataUrl }));
      return dataUrl;
    } catch (error) {
      console.error(`Erro ao buscar imagem do contato ${contatoId}:`, error);
      errorAvatarByContactIdRef.current[contatoId] = true;
      return '';
    } finally {
      loadingAvatarByContactIdRef.current[contatoId] = false;
      setLoadingAvatarByContactId((prev) => ({ ...prev, [contatoId]: false }));
    }
  }, [avatarByContactId]);

  useEffect(() => {
    if (!contatoSelecionado) return;
    carregarImagemContato(contatoSelecionado).catch(() => {});
  }, [carregarImagemContato, contatoSelecionado]);

  const enviarMensagem = async () => {
    const texto = mensagem.trim();
    if (!texto || !contatoSelecionado) return;
    const contatoId = getContatoIdSelecionado();

    if (!contatoId || String(contatoId).indexOf('tmp-') === 0) {
      showMessage('Contato invalido para envio de mensagem.', 'warning');
      return;
    }

    setEnviandoMensagem(true);
    try {
      await enviarMensagemTexto(contatoId, texto);
      setMensagem('');
      setShowEmojiPicker(false);
    } catch (error) {
      showMessage(`Erro ao enviar mensagem: ${error?.message || error}`, 'danger');
    } finally {
      setEnviandoMensagem(false);
    }
  };

  const handleAddEmoji = (emoji) => {
    setMensagem((prev) => `${prev}${emoji}`);
  };

  const handleAdicionarPedido = () => {
    if (!contatoSelecionado) return;
    const pedido = new Pedido();
    pedido.nomeCliente = contatoSelecionado?.nome || '';
    pedido.telefoneCliente = contatoSelecionado?.telefone || '';
    setPedidoSel(pedido);
    setIsModalCadPedidoOpen(true);
  };

  const handleEnviarUrlApp = async () => {
    const contatoId = getContatoIdSelecionado();

    if (!contatoId || String(contatoId).indexOf('tmp-') === 0) {
      showMessage('Contato inválido para envio da URL do aplicativo.', 'warning');
      return;
    }

    setEnviandoUrlApp(true);
    try {
      await enviarUrlApp(contatoId);
      showAlert('Link do aplicativo enviado com sucesso.', 'success');
    } catch (error) {
      showMessage(`Erro ao enviar o link do aplicativo: ${error?.message || error}`, 'danger');
    } finally {
      setEnviandoUrlApp(false);
    }
  };

  const handleSelecionarContato = useCallback(async (contato) => {
    await carregarImagemContato(contato);
    await selecionarContato(contato);
    setShowSidebarMobile(false);
  }, [carregarImagemContato, selecionarContato]);

  const handleAbrirModalEditarContato = () => {
    if (!contatoSelecionado) return;
    setNomeContatoEdicao(contatoSelecionado?.nome || '');
    setDesativarIAContatoEdicao(!!contatoSelecionado?.desativarIA);
    setShowModalEditarContato(true);
  };

  const handleFecharModalEditarContato = () => {
    if (savingContato) return;
    setShowModalEditarContato(false);
  };

  const handleSalvarContato = async () => {
    if (!contatoSelecionado) return;

    try {
      await updateChatContato(contatoSelecionado, nomeContatoEdicao, desativarIAContatoEdicao);
      setShowModalEditarContato(false);
    } catch {
      // tratamento visual no context
    }
  };

  const carregarMidiaMensagem = useCallback(async (messageId, tipoMensagem) => {
    if (!messageId) return;
    if (mediaByMessageId[messageId] || loadingMediaByMessageId[messageId]) return;

    setLoadingMediaByMessageId((prev) => ({ ...prev, [messageId]: true }));
    setErrorMediaByMessageId((prev) => ({ ...prev, [messageId]: false }));

    try {
      const result = await fetchData('/Whats/MediaBase64', { messageId, convertToMp4: false });
      const base64 = normalizarBase64(result?.base64);
      if (!base64) {
        throw new Error('Base64 vazio.');
      }
      const dataUrl = `data:${getMimeType(tipoMensagem)};base64,${base64}`;
      setMediaByMessageId((prev) => ({ ...prev, [messageId]: dataUrl }));
    } catch (error) {
      console.error(`Erro ao carregar midia da mensagem ${messageId}:`, error);
      setErrorMediaByMessageId((prev) => ({ ...prev, [messageId]: true }));
    } finally {
      setLoadingMediaByMessageId((prev) => ({ ...prev, [messageId]: false }));
    }
  }, [loadingMediaByMessageId, mediaByMessageId]);

  const renderConteudoMensagem = (msg) => {
    const tipoMensagem = Number(msg.tipoMensagem);
    const textoMidia = limparTextoMidia(msg.texto);

    if (msg.isDeleted) {
      return <p className="whatsapp-message-deleted">{msg.texto || 'Mensagem deletada'}</p>;
    }

    if (
      tipoMensagem === TIPO_MENSAGEM.IMAGEM ||
      tipoMensagem === TIPO_MENSAGEM.STICKER ||
      tipoMensagem === TIPO_MENSAGEM.AUDIO ||
      tipoMensagem === TIPO_MENSAGEM.VIDEO
    ) {
      if (!msg.idMessage) {
        return <p>{textoMidia || '(midia sem identificador)'}</p>;
      }

      const mediaDataUrl = msg.idMessage ? mediaByMessageId[msg.idMessage] : null;
      const mediaLoading = msg.idMessage ? !!loadingMediaByMessageId[msg.idMessage] : false;
      const mediaError = !!errorMediaByMessageId[msg.idMessage];

      return (
        <LazyMediaMessage
          msg={msg}
          textoMidia={textoMidia}
          mediaDataUrl={mediaDataUrl}
          mediaLoading={mediaLoading}
          mediaError={mediaError}
          onVisible={carregarMidiaMensagem}
        />
      );
    }

    if (tipoMensagem === TIPO_MENSAGEM.MENCAO) {
      const textoMencao = (msg.texto || '').trim();
      return (
        <div className="whatsapp-mention-content">
          {msg.mencaoRef ? (
            <div className={`whatsapp-mention-quote ${msg.mencaoRef.isDeleted ? 'deleted' : ''}`}>
              <small>{msg.mencaoRef.tipoLabel}</small>
              <p>{msg.mencaoRef.texto}</p>
            </div>
          ) : null}
          {textoMencao ? <p>{textoMencao}</p> : <p>(mencao sem texto)</p>}
        </div>
      );
    }

    return <p>{msg.texto || '(sem texto)'}</p>;
  };

  return (
    <div className="whatsapp-web-page">
      <div className={`whatsapp-web-layout ${showSidebarMobile ? 'show-sidebar-mobile' : ''}`}>
        <aside className="whatsapp-sidebar">
          <div className="whatsapp-sidebar-header">Conversas</div>

          <div className="whatsapp-search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Pesquisar ou começar nova conversa"
              value={filtroConversas}
              onChange={(event) => setFiltroConversas(event.target.value)}
            />
          </div>

          <div className="whatsapp-contact-list">
            {loadingContatos && (
              <div className="whatsapp-empty-contacts">Carregando contatos...</div>
            )}

            {!loadingContatos && conversasFiltradas.length === 0 && (
              <div className="whatsapp-empty-contacts">Nenhuma conversa encontrada.</div>
            )}

            {conversasFiltradas.map((contato) => (
              <button
                key={contato.id}
                className={`whatsapp-contact-item ${contatoSelecionado?.id === contato.id ? 'active' : ''}`}
                onClick={() => handleSelecionarContato(contato)}
              >
                <ContactAvatar
                  contato={contato}
                  avatarUrl={avatarByContactId[getContatoIdValue(contato)]}
                />
                <div className="whatsapp-contact-content">
                  <div className="whatsapp-contact-top">
                    <strong>{contato.nome || contato.telefone || 'Sem nome'}</strong>
                    <div className="whatsapp-contact-status">
                      <span className="whatsapp-contact-datetime">
                        {formatDataHoraContatoCompleta(contato.ultimaDataHora) || formatHorarioContato(contato.ultimaDataHora)}
                      </span>
                      {contato.hasUnread ? (
                        <span
                          className="whatsapp-contact-unread-badge"
                          aria-label="Contato com nova mensagem"
                          title="Contato com nova mensagem"
                        />
                      ) : null}
                    </div>
                  </div>
                  <p>{contato.ultimaMensagem || contato.telefone || 'Sem mensagens'}</p>
                </div>
              </button>
            ))}
          </div>
        </aside>

        <section className="whatsapp-chat-area">
          {contatoSelecionado ? (
            <>
              <header className="whatsapp-chat-header">
                <button
                  type="button"
                  className="whatsapp-mobile-back"
                  onClick={() => setShowSidebarMobile(true)}
                  title="Voltar para conversas"
                >
                  <FiArrowLeft />
                </button>
                <ContactAvatar
                  contato={contatoSelecionado}
                  avatarUrl={avatarByContactId[getContatoIdSelecionado()]}
                  large
                />
                <div>
                  <strong>{contatoSelecionado.nome || contatoSelecionado.telefone || 'Contato'}</strong>
                  <p>{contatoSelecionado.telefone || 'WhatsApp Web'}</p>
                </div>
                <button
                  type="button"
                  className="whatsapp-header-menu"
                  onClick={handleAbrirModalEditarContato}
                  title="Editar contato"
                >
                  ...
                </button>
              </header>

              <main className="whatsapp-messages" ref={mensagensRef}>
                {loadingMensagens ? (
                  <div className="whatsapp-empty-chat">Carregando mensagens...</div>
                ) : mensagensRenderizadas.length === 0 ? (
                  <div className="whatsapp-empty-chat">Nenhuma mensagem para este contato.</div>
                ) : (
                  mensagensRenderizadas.map((msg) => (
                    <div key={msg.id} className={`whatsapp-bubble ${msg.tipo === 'enviada' ? 'sent' : 'received'}`}>
                      {renderConteudoMensagem(msg)}
                      <span>{`${msg.isEdited ? 'editada · ' : ''}${msg.dataHoraLabel || msg.horario}`}</span>
                      {msg.reacoesLabel ? (
                        <div className="whatsapp-reaction-pill">{msg.reacoesLabel}</div>
                      ) : null}
                    </div>
                  ))
                )}
              </main>

              <footer className="whatsapp-input-area">
                <div className="emoji-picker-wrapper">
                  <button
                    type="button"
                    className="icon-button"
                    title="Emoji"
                    onClick={() => setShowEmojiPicker((prev) => !prev)}
                  >
                    <FaRegSmile />
                  </button>
                  {showEmojiPicker && (
                    <div className="emoji-picker">
                      {emojisDisponiveis.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="emoji-option"
                          onClick={() => handleAddEmoji(emoji)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" className="icon-button whatsapp-attach-button" title="Anexar arquivo">
                  <FiPaperclip />
                </button>
                <button type="button" className="icon-button whatsapp-image-button" title="Enviar imagem">
                  <FiImage />
                </button>

                <input
                  type="text"
                  value={mensagem}
                  onChange={(event) => setMensagem(event.target.value)}
                  onKeyDown={(event) => event.key === 'Enter' && enviarMensagem()}
                  placeholder="Digite uma mensagem"
                  disabled={enviandoMensagem}
                />

                

                {mensagem.trim() ? (
                  <button
                    type="button"
                    className="send-button"
                    onClick={enviarMensagem}
                    title="Enviar"
                    disabled={enviandoMensagem}
                  >
                    <FaPaperPlane />
                  </button>
                ) : (
                  <button type="button" className="send-button" title="Áudio">
                    <FaMicrophone />
                  </button>
                )}

                <button
                  type="button"
                  className="btn-add-order"
                  onClick={handleAdicionarPedido}
                  title="Adicionar Pedido"
                >
                  <FaPlus />
                  Adicionar Pedido
                </button>
                <button
                  type="button"
                  className="btn-send-app-url"
                  onClick={handleEnviarUrlApp}
                  title="Enviar link do aplicativo"
                  aria-label="Enviar link do aplicativo"
                  disabled={enviandoUrlApp}
                >
                  <FaLink />
                </button>
              </footer>
            </>
          ) : (
            <div className="whatsapp-empty-chat">
              {loadingContatos ? 'Carregando contatos...' : 'Selecione uma conversa para visualizar as mensagens.'}
            </div>
          )}
        </section>
      </div>
      <CadPedido
        isOpen={isModalCadPedidoOpen}
        onClose={() => setIsModalCadPedidoOpen(false)}
      />
      <ModalContainer show={showModalEditarContato} largura="420px" zIndex={1080}>
        <ModalHeader>
          <h5 className="modal-title">Dados do Contato</h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleFecharModalEditarContato}
            disabled={savingContato}
          />
        </ModalHeader>
        <ModalBody>
          <div className="whatsapp-edit-contact-form">
            <label htmlFor="nomeContatoEdicao" className="form-label">Nome do contato</label>
            <input
              id="nomeContatoEdicao"
              type="text"
              className="form-control"
              value={nomeContatoEdicao}
              onChange={(event) => setNomeContatoEdicao(event.target.value)}
              placeholder="Digite o nome do contato"
              disabled={savingContato}
            />
            <InputCheck
              id="desativarIAContatoEdicao"
              label="Desativar IA"
              checked={desativarIAContatoEdicao}
              onChange={(event) => setDesativarIAContatoEdicao(event.target.checked)}
              disabled={savingContato}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleFecharModalEditarContato}
            disabled={savingContato}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={handleSalvarContato}
            disabled={savingContato}
          >
            {savingContato ? 'Salvando...' : 'Salvar'}
          </button>
        </ModalFooter>
      </ModalContainer>
    </div>
  );
}

export default WhatsAppWeb;
